import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { invoiceService } from '../services/api';

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);

  // Send modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendForm, setSendForm] = useState({
    emailSubject: '',
    emailMessage: '',
  });

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const data = await invoiceService.getById(id);
      setInvoice(data);
      // Pre-populate send form
      setSendForm({
        emailSubject: `Invoice ${data.invoiceNumber} from CR Hood Solutions`,
        emailMessage: getDefaultEmailMessage(data),
      });
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultEmailMessage = (inv) => {
    const formattedAmount = '$' + Number(inv.amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const dueDate = inv.dueDate
      ? new Date(inv.dueDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : 'upon receipt';
    const clientName = inv.client?.name || inv.client?.company || 'Valued Client';

    return `Dear ${clientName},

Please find attached invoice ${inv.invoiceNumber} for ${formattedAmount}.

Payment is due by ${dueDate}. Please include the invoice number with your payment for proper credit.

If you have any questions about this invoice, please don't hesitate to reach out.

Thank you for your business!

Best regards,
Robin Hood
CR Hood Solutions`;
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    setError('');
    try {
      const updated = await invoiceService.update(id, { status: newStatus });
      setInvoice(updated);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update invoice status');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this invoice? This will also unbill any associated time entries.')) {
      return;
    }

    try {
      await invoiceService.delete(id);
      navigate('/invoices');
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError(err.response?.data?.message || 'Failed to delete invoice');
    }
  };

  const handleSendFormChange = (e) => {
    setSendForm({ ...sendForm, [e.target.name]: e.target.value });
  };

  const handleSendInvoice = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');

    try {
      const result = await invoiceService.send(id, sendForm.emailSubject, sendForm.emailMessage);
      setInvoice(result.invoice);
      setShowSendModal(false);
    } catch (err) {
      console.error('Error sending invoice:', err);
      setError(err.response?.data?.message || 'Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const handlePreviewPdf = () => {
    window.open(invoiceService.previewPdfUrl(id), '_blank');
  };

  const handleDownloadPdf = () => {
    window.open(invoiceService.downloadPdfUrl(id), '_blank');
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'badge-gray',
      sent: 'badge-info',
      paid: 'badge-success',
      overdue: 'badge-danger',
      cancelled: 'badge-gray',
    };
    return badges[status] || 'badge-gray';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500">Invoice not found</p>
        <Link to="/invoices" className="text-accent hover:underline mt-4 inline-block">
          Back to Invoices
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Invoice {invoice.invoiceNumber} | CR Hood Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/invoices" className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{invoice.invoiceNumber}</h1>
              {invoice.client && (
                <p className="text-sm text-gray-500">
                  <Link to={`/clients/${invoice.client.id}`} className="hover:text-accent">
                    {invoice.client.company || invoice.client.name}
                  </Link>
                </p>
              )}
            </div>
            <span className={`badge ${getStatusBadge(invoice.status)}`}>
              {invoice.status}
            </span>
          </div>

          <div className="flex items-center space-x-3">
            <button onClick={handlePreviewPdf} className="btn btn-secondary">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Preview PDF
            </button>
            <button onClick={handleDownloadPdf} className="btn btn-secondary">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
              <Link to={`/invoices/${id}/edit`} className="btn btn-secondary">
                Edit
              </Link>
            )}
            {invoice.status !== 'paid' && (
              <button onClick={handleDelete} className="btn btn-danger">
                Delete
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Invoice Summary Card */}
            <div className="card">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Invoice Details</h2>
                  <p className="text-sm text-gray-500">Invoice #{invoice.invoiceNumber}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-accent">{formatCurrency(invoice.amount)}</div>
                  {invoice.status === 'paid' && invoice.paidAt && (
                    <p className="text-sm text-green-600">Paid on {formatDate(invoice.paidAt)}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 border-t border-b">
                <div>
                  <div className="text-sm text-gray-500">Issue Date</div>
                  <div className="font-medium">{formatDate(invoice.issueDate)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Due Date</div>
                  <div className="font-medium">{formatDate(invoice.dueDate)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Client</div>
                  <div className="font-medium">
                    {invoice.client ? (
                      <Link to={`/clients/${invoice.client.id}`} className="text-accent hover:underline">
                        {invoice.client.company || invoice.client.name}
                      </Link>
                    ) : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Engagement</div>
                  <div className="font-medium">
                    {invoice.engagement ? (
                      <Link to={`/engagements/${invoice.engagement.id}`} className="text-accent hover:underline">
                        {invoice.engagement.title}
                      </Link>
                    ) : '—'}
                  </div>
                </div>
              </div>

              {invoice.notes && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Invoice Notes</h3>
                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                    {invoice.notes}
                  </div>
                </div>
              )}
            </div>

            {/* Time Entries */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">
                Time Entries ({invoice.timeEntries?.length || 0})
              </h2>

              {invoice.timeEntries && invoice.timeEntries.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {invoice.timeEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatDate(entry.date)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{entry.description}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-right">{entry.hours}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatCurrency(entry.rate)}</td>
                          <td className="px-4 py-3 text-sm font-medium text-right">
                            {formatCurrency(entry.hours * (entry.rate || 0))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-sm font-medium text-gray-900 text-right">Total</td>
                        <td className="px-4 py-3 text-sm font-bold text-right text-accent">
                          {formatCurrency(invoice.amount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-sm py-4">No time entries attached to this invoice.</p>
              )}
            </div>

            {/* Internal Notes */}
            {invoice.internalNotes && (
              <div className="card">
                <h2 className="text-lg font-semibold mb-4">Internal Notes</h2>
                <div className="bg-yellow-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap">
                  {invoice.internalNotes}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Actions</h2>
              <div className="space-y-2">
                {invoice.status === 'draft' && (
                  <>
                    <button
                      onClick={() => setShowSendModal(true)}
                      disabled={!invoice.client?.email}
                      className="btn btn-primary w-full"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Send Invoice
                    </button>
                    {!invoice.client?.email && (
                      <p className="text-xs text-red-500 text-center">Client has no email address</p>
                    )}
                    <button
                      onClick={() => handleStatusChange('sent')}
                      disabled={updating}
                      className="btn btn-secondary w-full"
                    >
                      {updating ? 'Updating...' : 'Mark as Sent (no email)'}
                    </button>
                  </>
                )}
                {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                  <button
                    onClick={() => handleStatusChange('paid')}
                    disabled={updating}
                    className="btn btn-success w-full"
                  >
                    {updating ? 'Updating...' : 'Mark as Paid'}
                  </button>
                )}
                {invoice.status === 'sent' && (
                  <button
                    onClick={() => handleStatusChange('overdue')}
                    disabled={updating}
                    className="btn btn-warning w-full"
                  >
                    {updating ? 'Updating...' : 'Mark as Overdue'}
                  </button>
                )}
                {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                  <button
                    onClick={() => handleStatusChange('cancelled')}
                    disabled={updating}
                    className="btn btn-secondary w-full"
                  >
                    {updating ? 'Updating...' : 'Cancel Invoice'}
                  </button>
                )}

                {/* Resend option for sent/overdue invoices */}
                {(invoice.status === 'sent' || invoice.status === 'overdue') && invoice.client?.email && (
                  <button
                    onClick={() => setShowSendModal(true)}
                    className="btn btn-secondary w-full mt-4"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Resend Invoice
                  </button>
                )}
              </div>
            </div>

            {/* Status History */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Status</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Current Status</span>
                  <span className={`badge ${getStatusBadge(invoice.status)}`}>
                    {invoice.status}
                  </span>
                </div>
                {invoice.paidAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Paid On</span>
                    <span className="text-sm font-medium text-green-600">{formatDate(invoice.paidAt)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Metadata */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Details</h2>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Invoice ID</dt>
                  <dd className="font-mono text-gray-900">#{invoice.id}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Created</dt>
                  <dd className="text-gray-900">{formatDate(invoice.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Last Updated</dt>
                  <dd className="text-gray-900">{formatDate(invoice.updatedAt)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Send Invoice Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Send Invoice</h2>
                <button
                  onClick={() => setShowSendModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Sending to: <span className="font-medium text-gray-900">{invoice.client?.email}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Attachment: <span className="font-medium text-gray-900">Invoice-{invoice.invoiceNumber}.pdf</span>
                </p>
              </div>

              <form onSubmit={handleSendInvoice} className="space-y-4">
                <div>
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    name="emailSubject"
                    value={sendForm.emailSubject}
                    onChange={handleSendFormChange}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Message</label>
                  <textarea
                    name="emailMessage"
                    value={sendForm.emailMessage}
                    onChange={handleSendFormChange}
                    rows={12}
                    className="form-input"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowSendModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sending}
                    className="btn btn-primary"
                  >
                    {sending ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Send Invoice
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
